import './Playground.css';
import '../common/PhotoUpload/PhotoUpload.css';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  deleteStreamVideo,
  getStreamPlaybackToken,
  getStreamUploadUrl,
  getStreamVideoStatus,
  StreamServiceError,
  uploadVideoToStream,
} from '../../services/streamService';
import { createImagePreview } from '../../utils/imageUtils';
import type { RecordQualityKey } from '../common/CameraRecorder/CameraRecorder';
import CameraRecorder from '../common/CameraRecorder/CameraRecorder';
import CameraUploadButton
  from '../common/CameraUploadButton/CameraUploadButton';
import CommentsBox from '../common/CommentsBox/CommentsBox';
import CommentsBoxReadonly
  from '../common/CommentsBoxReadonly/CommentsBoxReadonly';
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog';
import FullscreenPhotoViewer
  from '../common/FullscreenPhotoViewer/FullscreenPhotoViewer';
import IconButton from '../common/IconButton/IconButton';
import SkillJudge from '../common/SkillJudge/SkillJudge';
import TrashIcon from '../common/TrashIcon';
import VideoUploadButton from '../common/VideoUploadButton/VideoUploadButton';

// ---------------------------------------------------------------------------
// Local photo preview (no Firebase upload)
// ---------------------------------------------------------------------------

function PlaygroundLocalAthletePhoto(): React.ReactElement {
  const [preview, setPreview] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPreview(await createImagePreview(file));
    } catch (err) {
      console.error('Playground photo preview:', err);
    } finally {
      e.target.value = '';
    }
  };

  const clearPhoto = (): void => {
    setViewerOpen(false);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="photo-upload playground-local-athlete-photo">
      <div className="photo-upload-label">Athlete photo</div>
      {!preview ? (
        <div className="photo-upload-container">
          <CameraUploadButton
            className="photo-upload-button"
            onClick={() => fileInputRef.current?.click()}
            ariaLabel="Take photo or choose image"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="photo-upload-hidden-input"
            onChange={(e) => { void handleChange(e); }}
            aria-label="Take photo or choose image"
          />
        </div>
      ) : (
        <div className="photo-upload-preview">
          <button
            type="button"
            className="photo-upload-preview-open"
            onClick={() => setViewerOpen(true)}
            aria-label="Open Athlete photo full screen"
          >
            <img src={preview} alt="" />
          </button>
          <IconButton
            className="photo-upload-remove"
            aria-label="Remove photo"
            title="Remove photo"
            onClick={() => { setViewerOpen(false); setRemoveConfirmOpen(true); }}
          >
            <TrashIcon />
          </IconButton>
          <FullscreenPhotoViewer
            open={viewerOpen}
            src={preview}
            onClose={() => setViewerOpen(false)}
            viewerLabel="Athlete photo preview"
          />
          <ConfirmDialog
            open={removeConfirmOpen}
            title="Remove photo?"
            message="Are you sure you want to remove this photo?"
            confirmLabel="Remove"
            cancelLabel="Cancel"
            onConfirm={() => { setRemoveConfirmOpen(false); clearPhoto(); }}
            onCancel={() => setRemoveConfirmOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local video preview (no upload)
// ---------------------------------------------------------------------------

function PlaygroundLocalAthleteVideo(): React.ReactElement {
  const [preview, setPreview] = useState<string | null>(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearVideo = (): void => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="photo-upload">
      <div className="photo-upload-label">Athlete video</div>
      {!preview ? (
        <div className="photo-upload-container">
          <VideoUploadButton
            className="photo-upload-button"
            onClick={() => fileInputRef.current?.click()}
            ariaLabel="Record video or choose file"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            className="photo-upload-hidden-input"
            onChange={handleChange}
            aria-label="Record video or choose file"
          />
        </div>
      ) : (
        <div className="photo-upload-preview">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video className="playground-video-preview" src={preview} controls playsInline />
          <IconButton
            className="photo-upload-remove"
            aria-label="Remove video"
            title="Remove video"
            onClick={() => setRemoveConfirmOpen(true)}
          >
            <TrashIcon />
          </IconButton>
          <ConfirmDialog
            open={removeConfirmOpen}
            title="Remove video?"
            message="Are you sure you want to remove this video?"
            confirmLabel="Remove"
            cancelLabel="Cancel"
            onConfirm={() => { setRemoveConfirmOpen(false); clearVideo(); }}
            onCancel={() => setRemoveConfirmOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cloudflare Stream recorder
// ---------------------------------------------------------------------------

const STREAM_VIDEOS_KEY = 'playground_stream_videos';

interface StoredVideo {
  uid: string;
  iframeUrl: string;
  recordedAt: number;
  readyToStream: boolean;
}

function loadStoredVideos(): StoredVideo[] {
  try {
    const raw = localStorage.getItem(STREAM_VIDEOS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as StoredVideo[];
  } catch {
    return [];
  }
}

type StreamUploadStatus = 'idle' | 'uploading' | 'error';

const POLL_INTERVAL_MS = 3000;

interface VideoEntryProps {
  video: StoredVideo;
  localVideoUrl: string | null;
  onVideoReady: (uid: string) => void;
}

function StreamVideoEntry({ video, localVideoUrl, onVideoReady }: VideoEntryProps): React.ReactElement {
  const [freshIframeUrl, setFreshIframeUrl] = useState<string | null>(null);

  // Fetch a fresh signed playback URL every time this component mounts for a ready video.
  // Stored iframeUrls carry a 1-hour JWT — reusing them causes 401 "signature expired".
  useEffect(() => {
    if (!video.readyToStream) return;
    let cancelled = false;
    const fetchToken = async (): Promise<void> => {
      try {
        const { iframeUrl } = await getStreamPlaybackToken(video.uid);
        if (!cancelled) setFreshIframeUrl(iframeUrl);
      } catch {
        // ignore; user will see a blank player rather than an expired-token 401
      }
    };
    void fetchToken();
    return () => { cancelled = true; };
  }, [video.uid, video.readyToStream]);

  // Poll Cloudflare until readyToStream — justified useEffect: syncing with external system.
  useEffect(() => {
    if (video.readyToStream) return;

    let cancelled = false;

    const poll = async (): Promise<void> => {
      try {
        const { readyToStream } = await getStreamVideoStatus(video.uid);
        if (!cancelled && readyToStream) onVideoReady(video.uid);
      } catch {
        // ignore transient poll errors
      }
    };

    void poll();
    const id = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [video.uid, video.readyToStream, onVideoReady]);

  if (video.readyToStream) {
    if (!freshIframeUrl) return <></>;
    return (
      <div className="playground-stream-video-wrapper">
        <iframe
          src={`${freshIframeUrl}?autoplay=false`}
          className="playground-video-preview"
          allow="accelerometer; gyroscope; encrypted-media;"
          allowFullScreen
          title="Athlete video"
        />
      </div>
    );
  }

  if (localVideoUrl) {
    return (
      <div className="playground-stream-video-wrapper">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video className="playground-video-preview" src={localVideoUrl} controls playsInline />
        {/* <p className="playground-stream-status-text" aria-live="polite">
          <span className="playground-stream-processing-dot" aria-hidden="true" />
          Processing…
        </p> */}
      </div>
    );
  }

  return (<></>
  );
}

function PlaygroundStreamCameraRecorder(): React.ReactElement {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [recordQuality, setRecordQuality] = useState<RecordQualityKey>('medium');
  const [uploadStatus, setUploadStatus] = useState<StreamUploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingVideoUrl, setPendingVideoUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [videos, setVideos] = useState<StoredVideo[]>(() => loadStoredVideos());
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  // objectURLs are in-memory only — not persisted to localStorage.
  // Keyed by video uid; entries are revoked and removed when the video becomes ready or is removed.
  const localVideoUrlsRef = useRef<Map<string, string>>(new Map());

  const updateVideos = useCallback((updater: (prev: StoredVideo[]) => StoredVideo[]): void => {
    setVideos((prev) => {
      const next = updater(prev);
      localStorage.setItem(STREAM_VIDEOS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const markVideoReady = useCallback((uid: string): void => {
    const url = localVideoUrlsRef.current.get(uid);
    if (url) { URL.revokeObjectURL(url); localVideoUrlsRef.current.delete(uid); }
    updateVideos((prev) => prev.map((v) => (v.uid === uid ? { ...v, readyToStream: true } : v)));
  }, [updateVideos]);

  const removeVideo = useCallback((uid: string): void => {
    const url = localVideoUrlsRef.current.get(uid);
    if (url) { URL.revokeObjectURL(url); localVideoUrlsRef.current.delete(uid); }
    updateVideos((prev) => prev.filter((v) => v.uid !== uid));
  }, [updateVideos]);

  const startUpload = async (blob: Blob, mimeType: string, localUrl: string): Promise<void> => {
    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    try {
      const { uploadUrl, uid } = await getStreamUploadUrl();
      await uploadVideoToStream(uploadUrl, blob, mimeType, (p) => setUploadProgress(p));
      const { iframeUrl } = await getStreamPlaybackToken(uid);
      localVideoUrlsRef.current.set(uid, localUrl);
      setPendingVideoUrl(null);
      updateVideos((prev) => [{ uid, iframeUrl, recordedAt: Date.now(), readyToStream: false }, ...prev]);
      setUploadStatus('idle');
    } catch (err) {
      URL.revokeObjectURL(localUrl);
      setPendingVideoUrl(null);
      setErrorMessage(
        err instanceof StreamServiceError ? err.message : 'Upload failed. Please try again.',
      );
      setUploadStatus('error');
    }
  };

  const handleCapture = (blob: Blob, mimeType: string): void => {
    const localUrl = URL.createObjectURL(blob);
    setPendingVideoUrl(localUrl);
    setCameraOpen(false);
    void startUpload(blob, mimeType, localUrl);
  };

  const handleRemoveConfirmed = async (): Promise<void> => {
    const current = videos[0];
    if (!current) return;
    try {
      await deleteStreamVideo(current.uid);
    } catch {
      // remove locally regardless of server error
    }
    removeVideo(current.uid);
  };

  // Revoke all remaining objectURLs on unmount.
  useEffect(() => {
    const map = localVideoUrlsRef.current;
    return () => { map.forEach((url) => URL.revokeObjectURL(url)); };
  }, []);

  const currentVideo = videos[0] ?? null;
  const hasActiveVideo = pendingVideoUrl !== null || currentVideo !== null;
  const isUploading = uploadStatus === 'uploading';

  return (
    <div className="playground-stream-section">
      <p className="playground-demo-hint">
        <code>CameraRecorder</code> — records via MediaRecorder and uploads directly to
        Cloudflare Stream (private, signed URL). Requires owner sign-in.
      </p>

      <label className="playground-stream-quality-label" htmlFor="stream-quality">Quality</label>
      <select
        id="stream-quality"
        className="playground-stream-quality-select"
        value={recordQuality}
        onChange={(e) => setRecordQuality(e.target.value as RecordQualityKey)}
        disabled={isUploading}
      >
        <option value="low">Low – 1 Mbps (~3.8 MB / 30 s)</option>
        <option value="medium">Medium – 2.5 Mbps (~9 MB / 30 s)</option>
        <option value="high">High – 5 Mbps (~19 MB / 30 s)</option>
        <option value="max">Max – 8 Mbps (~30 MB / 30 s)</option>
      </select>

      <div className="photo-upload">
        <div className="photo-upload-label">Recorded video</div>

        {!hasActiveVideo ? (
          <div className="photo-upload-container">
            <VideoUploadButton onClick={() => setCameraOpen(true)} ariaLabel="Record video" />
          </div>
        ) : (
          <div className="photo-upload-preview">
            {pendingVideoUrl ? (
              <div className="playground-stream-video-wrapper">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video className="playground-video-preview" src={pendingVideoUrl} controls playsInline />
                <p className="playground-stream-status-text" aria-live="polite">
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3" />
                    <circle
                      cx="12" cy="12" r="10" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeDasharray="62.83"
                      strokeDashoffset={62.83 * (1 - uploadProgress / 100)}
                      transform="rotate(-90 12 12)"
                      style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                  </svg>
                  Uploading {uploadProgress}%
                </p>
              </div>
            ) : currentVideo ? (
              <StreamVideoEntry
                video={currentVideo}
                localVideoUrl={localVideoUrlsRef.current.get(currentVideo.uid) ?? null}
                onVideoReady={markVideoReady}
              />
            ) : null}

            <IconButton
              className="photo-upload-remove"
              aria-label="Remove video"
              title="Remove video"
              disabled={isUploading}
              onClick={() => setRemoveConfirmOpen(true)}
            >
              <TrashIcon />
            </IconButton>

            <ConfirmDialog
              open={removeConfirmOpen}
              title="Remove video?"
              message="Are you sure you want to remove this video?"
              confirmLabel="Remove"
              cancelLabel="Cancel"
              onConfirm={() => { setRemoveConfirmOpen(false); void handleRemoveConfirmed(); }}
              onCancel={() => setRemoveConfirmOpen(false)}
            />
          </div>
        )}

        {uploadStatus === 'error' && (
          <p className="playground-stream-error">{errorMessage}</p>
        )}
      </div>

      {cameraOpen && (
        <CameraRecorder
          quality={recordQuality}
          onCapture={handleCapture}
          onClose={() => setCameraOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill judge demo
// ---------------------------------------------------------------------------

const PlaygroundSkillJudgeDemo: React.FC = () => {
  const [scale, setScale] = useState(0);
  const [toeTouch, setToeTouch] = useState(3);
  const [notes, setNotes] = useState('');

  return (
    <div className="playground-demo">
      <p className="playground-demo-hint">
        CheerEval-styled <code>SkillJudge</code> — hover the &quot;?&quot; for the
        judging scale. Ratings 0–5 (0 = not rated).
      </p>
      <SkillJudge title="Scale" value={scale} onChange={setScale} />
      <SkillJudge title="Toe Touch" value={toeTouch} onChange={setToeTouch} />
      <SkillJudge title="Read-only (value 4)" value={4} />
      <CommentsBox
        value={notes}
        onChange={setNotes}
        placeholder="Try blur or Save…"
        onSave={async () => {
          await new Promise<void>((r) => { setTimeout(r, 400); });
        }}
      />
      <CommentsBoxReadonly title="Same notes (read-only)" value={notes} />

      <div className="playground-local-athlete-photo-section">
        <p className="playground-demo-hint">
          Same <code>CameraUploadButton</code> + file input as Add Athlete — preview
          only here (no Firebase). Tap the photo for <code>FullscreenPhotoViewer</code>.
        </p>
        <PlaygroundLocalAthletePhoto />
      </div>

      <div className="playground-local-athlete-video-section">
        <p className="playground-demo-hint">
          <code>VideoUploadButton</code> — triggers the rear camera on iOS/Android via{' '}
          <code>capture=&quot;environment&quot;</code>. Preview only here (no Firebase).
        </p>
        <PlaygroundLocalAthleteVideo />
      </div>

      <div className="playground-stream-recorder-section">
        <PlaygroundStreamCameraRecorder />
      </div>

      <p className="playground-demo-values">
        scale={scale}, toeTouch={toeTouch}, notes={JSON.stringify(notes)}
      </p>
    </div>
  );
};

export default PlaygroundSkillJudgeDemo;
